import { LanguageObject } from '@/types';

import { getNameFromLanguage, itemByLanguage } from './language';

import {
  BaseReferenceDto,
  ChecklistSectionStepDto,
  PlanStep,
} from '@/_generated/api-clients/xp';

/**
 * @deprecated Reads reference name from ChecklistSectionStepDto; migrate callers to use PlanStep.resource.references directly. Will be removed in PATHWAY-3174.
 * This will not have a replacement since reference is not on PlanStep root.
 */
export const getStepReferenceName = (
  step: ChecklistSectionStepDto,
  language: string,
) => {
  const stepReference = getNameFromLanguage(
    step.reference?.references as LanguageObject,
    language,
    true,
  ) as BaseReferenceDto;
  const stepReferenceName =
    stepReference.name ??
    getNameFromLanguage(step.reference?.name as LanguageObject, language, true);
  return stepReferenceName;
};

/**
 * @deprecated Handles both PlanStep and ChecklistSectionStepDto; migrate callers to use PlanStep.resource.references directly. Will be removed in PATHWAY-3174.
 * Use getPlanStepResourceReferenceName for PlanStep and getStepReferenceName for ChecklistSectionStepDto instead. This function exists to handle the legacy ChecklistSectionStepDto shape that contains a reference object, as well as the newer PlanStep shape where references are nested under resource. Will be removed once all consumers have migrated to the PlanStep shape.
 */
export const getStepResourceReferenceName = (
  step: PlanStep | ChecklistSectionStepDto,
  language: string,
) => {
  if ('resource' in step && step.resource?.references) {
    const matchingReference = step.resource.references.find(
      ref => ref.language.toLowerCase() === language.toLowerCase(),
    );
    return matchingReference?.name ?? step.resource.references[0]?.name;
  }
  return getStepReferenceName(step as ChecklistSectionStepDto, language);
};

export const getPlanStepResourceReferenceName = (
  step: PlanStep,
  language: string,
) =>
  step.resource?.references?.find(
    ref => ref.language.toLowerCase() === language.toLowerCase(),
  )?.name ??
  step.resource?.references?.[0]?.name ??
  null;

/**
 * @deprecated Reads step name from ChecklistSectionStepDto; migrate callers to derive name from PlanStep directly. Will be removed in PATHWAY-3174.
 * Use getPlanStepResourceReferenceName for PlanStep and getStepReferenceName for ChecklistSectionStepDto instead. This function exists to handle the legacy ChecklistSectionStepDto shape that contains a reference object, as well as the newer PlanStep shape where references are nested under resource. Will be removed once all consumers have migrated to the PlanStep shape.
 */
export const getStepNameByLang = (
  step: ChecklistSectionStepDto,
  language: string,
  strict?: boolean,
) => {
  const stepReferenceName = getStepReferenceName(step, language);
  const stepName = getNameFromLanguage(
    step.name as LanguageObject,
    language,
    strict,
  );
  return !stepReferenceName ? stepName : stepReferenceName;
};

export const getPlanStepNameByLang = (step: PlanStep, language: string) => {
  const stepResourceReferenceName = getPlanStepResourceReferenceName(
    step,
    language,
  );
  if (stepResourceReferenceName) {
    return stepResourceReferenceName;
  }
  return itemByLanguage(step.name, language);
};
